import type { CSSProperties } from 'react'
import { cn } from '@/lib/utils'

export type WorkspaceListRowChromeOptions = {
  /**
   * When false, omit the colored left rail (Home single-workspace filter).
   * Default true when a color is present (Browse / sidebar).
   */
  showRail?: boolean
}

/**
 * Browse-aligned row chrome: workspace-colored left border on unselected rows
 * when `showRail` is true (Home with 2+ workspaces).
 * Selected chrome is owned by the row (`HomeFeedRow` / `ExtensionListRow`):
 * primary wash + 2px primary left bar. Do not stack a second rail or muted wash.
 */
export function workspaceListRowChrome(
  colorHex: string | undefined,
  active: boolean,
  opts?: WorkspaceListRowChromeOptions,
): { className: string; style?: CSSProperties } {
  const showRail = opts?.showRail !== false && Boolean(colorHex)

  if (active) {
    return { className: '' }
  }

  if (!showRail || !colorHex) {
    return { className: 'hover:bg-accent/40' }
  }

  return {
    className: 'border-l-2 hover:bg-accent/40',
    style: { borderLeftColor: `${colorHex}55` },
  }
}

/** Organizations store `color` as CSS hex (`#6366f1`). Use inline styles, not Tailwind classes. */
export function WorkspaceAccentLabel({
  label,
  colorHex,
  className,
  size = 'list',
}: {
  label: string
  /** CSS color string (typically hex from `/api/organizations`) */
  colorHex: string
  className?: string
  size?: 'list' | 'reader'
}) {
  const isList = size === 'list'

  return (
    <span
      className={cn(
        'inline-flex min-w-0 max-w-[10rem] items-center gap-1 font-medium leading-none shrink-0',
        isList ? 'text-[10px]' : 'text-sm',
        className,
      )}
      title={label}
      data-workspace-accent
    >
      <span
        className={cn(
          'shrink-0 rounded-full',
          isList ? 'h-1.5 w-1.5' : 'h-2 w-2',
        )}
        style={{ backgroundColor: colorHex }}
        aria-hidden
      />
      <span className="min-w-0 truncate text-muted-foreground/70">{label}</span>
    </span>
  )
}
